import { dom } from "../dom";
import { state } from "../state";
import { ifcLoader } from "../viewer/scene";
import { getIfcName, unwrapIfcValue } from "../ifc/props";
import { escapeHtmlText } from "../utils/html";

/**
 * Универсальное приведение IFC-значения к человеко-читаемой строке.
 * Покрывает основные обёртки web-ifc: { value }, { Value }, { Name },
 * массивы, IfcQuantity*, IfcPropertySingleValue.
 */
function ifcValueToString(raw: any): string {
  if (raw === null || raw === undefined) {
    return "—";
  }
  if (Array.isArray(raw)) {
    return raw.map((item) => ifcValueToString(item)).join(", ");
  }
  if (typeof raw === "object") {
    const direct = unwrapIfcValue(raw);
    if (direct) {
      return direct;
    }
    if (typeof raw.Name === "object" || typeof raw.Name === "string") {
      const nameStr = unwrapIfcValue(raw.Name);
      if (nameStr) {
        return nameStr;
      }
    }
    const nominal =
      raw.NominalValue ?? raw.Nominalvalue ?? raw.Value ?? raw.LengthValue ?? raw.AreaValue ??
      raw.VolumeValue ?? raw.CountValue ?? raw.WeightValue ?? raw.NumberValue;
    if (nominal !== undefined && nominal !== null) {
      const nominalStr = ifcValueToString(nominal);
      if (nominalStr && nominalStr !== "—") {
        return nominalStr;
      }
    }
    try {
      return JSON.stringify(raw);
    } catch (_) {
      return String(raw);
    }
  }
  return String(raw);
}

function renderKvTable(rows: Array<[string, string]>): string {
  if (rows.length === 0) {
    return '<p class="muted" style="margin:0; font-size:11px;">Нет данных.</p>';
  }
  return (
    '<table class="prop-table">' +
    rows
      .map(
        ([k, v]) =>
          `<tr><td class="k">${escapeHtmlText(k)}</td><td class="v">${escapeHtmlText(v)}</td></tr>`,
      )
      .join("") +
    "</table>"
  );
}

interface PropertySetView {
  name: string;
  rows: Array<[string, string]>;
}

function readPropertySetEntries(pset: any): PropertySetView {
  const name = unwrapIfcValue(pset?.Name) || "PropertySet";
  const rows: Array<[string, string]> = [];
  const properties = pset?.HasProperties || pset?.Quantities || [];
  for (const prop of properties) {
    const propName = unwrapIfcValue(prop?.Name) || "(noname)";
    const propValue = ifcValueToString(
      prop?.NominalValue ?? prop?.Nominalvalue ?? prop?.Value ?? prop?.LengthValue ??
      prop?.AreaValue ?? prop?.VolumeValue ?? prop?.CountValue ?? prop?.WeightValue ?? prop,
    );
    rows.push([propName, propValue]);
  }
  return { name, rows };
}

async function fetchMaterials(modelId: number, expressId: number): Promise<string[]> {
  const out: string[] = [];
  try {
    const mats = await (ifcLoader.ifcManager as any).getMaterialsProperties?.(
      modelId,
      expressId,
      true,
    );
    if (Array.isArray(mats)) {
      for (const mat of mats) {
        const name = unwrapIfcValue(mat?.Name);
        if (name) {
          out.push(name);
        } else if (Array.isArray(mat?.ForLayerSet?.MaterialLayers)) {
          for (const layer of mat.ForLayerSet.MaterialLayers) {
            const layerName = unwrapIfcValue(layer?.Material?.Name);
            if (layerName) {
              out.push(layerName);
            }
          }
        } else if (Array.isArray(mat?.MaterialLayers)) {
          for (const layer of mat.MaterialLayers) {
            const layerName = unwrapIfcValue(layer?.Material?.Name);
            if (layerName) {
              out.push(layerName);
            }
          }
        }
      }
    }
  } catch (_) {
    /* ignore */
  }
  return out;
}

export async function refreshPropertiesPanel(): Promise<void> {
  if (!state.ifcModel || state.selectedExpressIds.size === 0) {
    dom.propertiesRoot.innerHTML =
      '<p class="muted" style="margin:0; font-size:12px;">Выделите элемент в сцене.</p>';
    return;
  }
  if (state.selectedExpressIds.size > 1) {
    dom.propertiesRoot.innerHTML =
      `<p class="muted" style="margin:0; font-size:12px;">Выделено элементов: ${state.selectedExpressIds.size}. Свойства показываются только для одного выделенного элемента.</p>`;
    return;
  }
  const expressId = state.selectedExpressIds.values().next().value as number;
  const modelId = state.ifcModel.modelID;

  let type = "Unknown";
  let props: any = null;
  try {
    const rawType = ifcLoader.ifcManager.getIfcType(modelId, expressId) as
      | string
      | Promise<string>;
    type = (typeof rawType === "string" ? rawType : await rawType) || "Unknown";
  } catch (_) {
    type = "Unknown";
  }
  try {
    props = await ifcLoader.ifcManager.getItemProperties(modelId, expressId, false);
  } catch (_) {
    /* keep null */
  }

  const generalRows: Array<[string, string]> = [];
  generalRows.push(["Express ID", String(expressId)]);
  generalRows.push(["Тип IFC", type]);
  if (props) {
    const globalId = unwrapIfcValue(props.GlobalId);
    if (globalId) {
      generalRows.push(["GlobalId", globalId]);
    }
    const name = getIfcName(props);
    if (name) {
      generalRows.push(["Name", name]);
    }
    const tag = unwrapIfcValue(props.Tag);
    if (tag) {
      generalRows.push(["Tag", tag]);
    }
    const description = unwrapIfcValue(props.Description);
    if (description) {
      generalRows.push(["Description", description]);
    }
    const objectType = unwrapIfcValue(props.ObjectType);
    if (objectType) {
      generalRows.push(["ObjectType", objectType]);
    }
    const predefinedType = unwrapIfcValue(props.PredefinedType);
    if (predefinedType) {
      generalRows.push(["PredefinedType", predefinedType]);
    }
  }

  const meta = state.elementMeta.get(expressId);
  if (meta?.storeyName) {
    generalRows.push(["Этаж", meta.storeyName]);
  }

  let psetsHtml = "";
  let qsetsHtml = "";
  try {
    const propertySets = await ifcLoader.ifcManager.getPropertySets(modelId, expressId, true);
    const psets: PropertySetView[] = [];
    const qsets: PropertySetView[] = [];
    for (const pset of propertySets || []) {
      const psetType = String((pset as any)?.type || "").toUpperCase();
      const view = readPropertySetEntries(pset);
      if (psetType.includes("QUANTITY")) {
        qsets.push(view);
      } else {
        psets.push(view);
      }
    }
    psetsHtml = psets
      .map(
        (p) =>
          `<div class="prop-pset"><div class="prop-pset-title">${escapeHtmlText(p.name)}</div>${renderKvTable(p.rows)}</div>`,
      )
      .join("");
    qsetsHtml = qsets
      .map(
        (q) =>
          `<div class="prop-pset"><div class="prop-pset-title">${escapeHtmlText(q.name)}</div>${renderKvTable(q.rows)}</div>`,
      )
      .join("");
  } catch (_) {
    /* ignore */
  }

  const materials = await fetchMaterials(modelId, expressId);

  const sections: string[] = [];
  sections.push(
    `<div class="prop-section"><h3 class="prop-section-head">Общее</h3>${renderKvTable(generalRows)}</div>`,
  );
  if (materials.length > 0) {
    sections.push(
      `<div class="prop-section"><h3 class="prop-section-head">Материалы</h3>${renderKvTable(materials.map((m, i) => [`Материал ${i + 1}`, m]))}</div>`,
    );
  }
  if (qsetsHtml) {
    sections.push(`<div class="prop-section"><h3 class="prop-section-head">Quantity Sets</h3>${qsetsHtml}</div>`);
  }
  if (psetsHtml) {
    sections.push(`<div class="prop-section"><h3 class="prop-section-head">Property Sets</h3>${psetsHtml}</div>`);
  }

  dom.propertiesRoot.innerHTML = sections.join("");
}
